import { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import './Table.css';

/**
 * Reusable Table component with sorting and row click handlers
 *
 * @param {Object} props
 * @param {Array} props.columns - Array of column configurations
 * @param {Array} props.data - Array of data objects to display
 * @param {Function} props.onRowClick - Handler for row click events
 * @param {boolean} props.sortable - Whether table supports sorting
 * @param {string} props.emptyMessage - Message to display when no data
 */
const Table = ({
  columns = [],
  data = [],
  onRowClick,
  sortable = true,
  emptyMessage = 'No data available',
  className = '',
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  const handleSort = (columnKey) => {
    if (!sortable) return;

    const column = columns.find((col) => col.key === columnKey);
    if (column && column.sortable === false) return;

    let direction = 'asc';
    if (sortConfig.key === columnKey && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    setSortConfig({ key: columnKey, direction });
  };

  const sortedData = useMemo(() => {
    const sorted = [...data];
    if (sortConfig.key && sortConfig.direction) {
      sorted.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        if (typeof aValue === 'string' || typeof bValue === 'string') {
          const aStr = String(aValue);
          const bStr = String(bValue);
          return sortConfig.direction === 'asc'
            ? aStr.localeCompare(bStr, undefined, { sensitivity: 'base' })
            : bStr.localeCompare(aStr, undefined, { sensitivity: 'base' });
        }

        if (sortConfig.direction === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
    }
    return sorted;
  }, [data, sortConfig]);

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) return '⇅';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const handleRowClick = (row, index) => {
    if (onRowClick) {
      onRowClick(row, index);
    }
  };

  return (
    <div className={`table-container ${className}`}>
      <table className="table">
        <thead className="table-header">
          <tr>
            {columns.map((column) => {
              const isSortable = sortable && column.sortable !== false;
              return (
                <th
                  key={column.key}
                  className={`table-header-cell ${isSortable ? 'table-header-cell--sortable' : ''}`}
                  onClick={() => handleSort(column.key)}
                  onKeyDown={(e) => {
                    if (isSortable && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      handleSort(column.key);
                    }
                  }}
                  tabIndex={isSortable ? 0 : undefined}
                  role={isSortable ? 'button' : undefined}
                  aria-sort={
                    sortConfig.key === column.key
                      ? sortConfig.direction === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : undefined
                  }
                  style={{ width: column.width }}
                >
                  <div className="table-header-content">
                    <span>{column.label}</span>
                    {isSortable && <span className="table-sort-icon">{getSortIcon(column.key)}</span>}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="table-body">
          {sortedData.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="table-empty">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            sortedData.map((row, rowIndex) => (
              <tr
                key={row.id ?? rowIndex}
                className={`table-row ${onRowClick ? 'table-row--clickable' : ''}`}
                onClick={() => handleRowClick(row, rowIndex)}
                onKeyDown={(e) => {
                  if (onRowClick && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    handleRowClick(row, rowIndex);
                  }
                }}
                tabIndex={onRowClick ? 0 : undefined}
                role={onRowClick ? 'button' : undefined}
              >
                {columns.map((column) => (
                  <td key={column.key} className="table-cell">
                    {column.render ? column.render(row[column.key], row) : row[column.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

Table.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      sortable: PropTypes.bool,
      width: PropTypes.string,
      render: PropTypes.func,
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  onRowClick: PropTypes.func,
  sortable: PropTypes.bool,
  emptyMessage: PropTypes.string,
  className: PropTypes.string,
};

export default Table;
